export interface ProviderEncounterVM {
  date?: string | null;
  treatment?: string | null;
  amountBilled?: string | null;
}

export interface ProviderGroupViewModel {
  provider: string;
  encounters: ProviderEncounterVM[];
  subtotalDisplay: string;
}

function parseMoneyToNumber(display?: string | null): number {
  if (!display) return 0;
  const num = parseFloat(String(display).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(num) ? num : 0;
}

function formatMoney(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

function normaliseProviderName(name?: string | null): string {
  if (!name) return '';
  // Simple facility-level normalizer; backend performs LLM-based canonicalization as well
  return name
    .replace(/\b(inc|llc|pc)\b\.?/gi, '')
    .replace(/\(.*?\)/g, '')
    .trim()
    .replace(/\s{2,}/g, ' ');
}

export function toProviderGroups(ctx: any): ProviderGroupViewModel[] {
  const groups = new Map<string, ProviderGroupViewModel>();
  const rows: any[] = Array.isArray(ctx?.medical_chronology)
    ? ctx.medical_chronology
    : [];

  rows.forEach((raw) => {
    const providerRaw = (raw?.provider ?? raw?.treatment_provider) as
      | string
      | undefined;
    const provider = normaliseProviderName(providerRaw);
    if (!provider) {
      return;
    }
    const amount = (raw?.amount_billed ?? raw?.total_billed ?? '') as
      | string
      | undefined;
    const vm: ProviderEncounterVM = {
      date: (raw?.date ?? raw?.treatment_date) || null,
      treatment: (raw?.treatment ?? raw?.description) || null,
      amountBilled: amount || null,
    };
    if (!groups.has(provider)) {
      groups.set(provider, {
        provider,
        encounters: [],
        subtotalDisplay: '$0.00',
      });
    }
    groups.get(provider)!.encounters.push(vm);
  });

  // compute subtotals
  Array.from(groups.values()).forEach((g) => {
    const total = g.encounters.reduce(
      (sum, e) => sum + parseMoneyToNumber(e.amountBilled),
      0,
    );
    g.subtotalDisplay = formatMoney(total);
  });

  return Array.from(groups.values()).sort((a, b) =>
    a.provider.localeCompare(b.provider),
  );
}
